import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { Activity, BookOpen, Building2, ChevronRight, FileSignature, Gavel, Hash, MapPin, Pencil, ShieldCheck, Trophy, Truck } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useProjectActivity } from '../../../../hooks/useProjectActivity';
import { useProjectVolumes } from '../../../../hooks/useProjectVolumes';
import { useSolicitationInsights } from '../../../../hooks/useSolicitationInsights';
import { projectDataClient } from '../../../../lib/connect/clients';
import { EditableField } from '../../../common/EditableField';
import { LifecycleNodeKind, LinkedChips } from '../../../LinkedChips';
import { DeadlineCountdown } from './DeadlineCountdown';
import { MilestonesPanel } from './MilestonesPanel';
import { AGENCY_OPTIONS, CONTRACT_TYPE_OPTIONS, SET_ASIDE_OPTIONS, VEHICLE_OPTIONS } from './metadataOptions';
import { NAICS_CODES } from './naicsCodes';
import { ProposalRfiResponsesPanel } from './ProposalRfiResponsesPanel';
/**
 * Right half of the V2 Home page. Project briefing — answers "what is this
 * proposal, when is it due, who's on the hook, and what's the eval method?"
 *
 * Sections (top to bottom):
 *   1. Big deadline countdown card (hero)
 *   2. Solicitation summary chips (agency, sol#)
 *   3. Evaluation scheme — method + factors
 *   4. Volumes — list with custom badges (PR8/PR9 will add progress bars)
 *   5. Team activity feed (last 5)
 */ export function ProjectBriefingPanel({ projectId, projectName, dueDate, solicitation }) {
    const insights = useSolicitationInsights(projectId);
    const volumes = useProjectVolumes(projectId);
    const activity = useProjectActivity(projectId, {
        pageSize: 5
    });
    const evalScheme = insights.data?.evaluationScheme;
    const sol = solicitation;
    const queryClient = useQueryClient();
    const handleFieldSave = useCallback(async (field, value)=>{
        try {
            await projectDataClient.updateProject({
                projectId,
                [field]: value
            });
            // Invalidate the project query so the UI refreshes with the new value.
            queryClient.invalidateQueries({
                queryKey: [
                    'proposal-project',
                    projectId
                ]
            });
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to update ${field}:`, err);
        }
    }, [
        projectId,
        queryClient
    ]);
    const [editingName, setEditingName] = useState(false);
    const [nameValue, setNameValue] = useState(projectName || '');
    // Sync nameValue when projectName changes (e.g. after save + refetch).
    useEffect(()=>{
        if (projectName) setNameValue(projectName);
    }, [
        projectName
    ]);
    const saveName = useCallback(async ()=>{
        const trimmed = nameValue.trim();
        if (!trimmed || trimmed === projectName) {
            setEditingName(false);
            return;
        }
        await handleFieldSave('name', trimmed);
        setEditingName(false);
    }, [
        nameValue,
        projectName,
        handleFieldSave,
        setEditingName
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col gap-4 overflow-auto p-4 sm:p-6 lg:p-8",
        children: [
            /*#__PURE__*/ _jsxs("header", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400",
                        children: "Project"
                    }),
                    editingName ? /*#__PURE__*/ _jsx("input", {
                        type: "text",
                        value: nameValue,
                        onChange: (e)=>setNameValue(e.target.value),
                        onBlur: saveName,
                        onKeyDown: (e)=>{
                            if (e.key === 'Enter') saveName();
                            if (e.key === 'Escape') {
                                setNameValue(projectName || '');
                                setEditingName(false);
                            }
                        },
                        ref: (el)=>el?.focus(),
                        className: "w-full rounded-md border border-blue-300 bg-white px-2 py-1 text-lg font-semibold text-slate-900 outline-none ring-2 ring-blue-100"
                    }) : /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        onClick: ()=>setEditingName(true),
                        className: "group flex w-full items-baseline gap-2 text-left",
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                className: "text-lg font-semibold text-slate-900",
                                children: projectName || 'Untitled Project'
                            }),
                            /*#__PURE__*/ _jsx(Pencil, {
                                className: "h-3 w-3 flex-shrink-0 text-slate-300 opacity-0 transition-opacity group-hover:opacity-100"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(LinkedChips, {
                kind: LifecycleNodeKind.PROPOSAL,
                id: projectId
            }),
            /*#__PURE__*/ _jsx(ProposalRfiResponsesPanel, {
                proposalProjectId: projectId
            }),
            /*#__PURE__*/ _jsx(DeadlineCountdown, {
                dueDate: dueDate,
                variant: "card"
            }),
            /*#__PURE__*/ _jsx(MilestonesPanel, {
                projectId: projectId
            }),
            sol && /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-2",
                children: [
                    sol.solicitationNumber && /*#__PURE__*/ _jsx(SummaryChip, {
                        icon: /*#__PURE__*/ _jsx(Gavel, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "Solicitation",
                        value: sol.solicitationNumber
                    }),
                    /*#__PURE__*/ _jsx(EditableField, {
                        icon: /*#__PURE__*/ _jsx(Building2, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "Agency",
                        value: sol.agency,
                        options: AGENCY_OPTIONS,
                        onSave: (v)=>handleFieldSave('agency', v),
                        placeholder: "Search agencies..."
                    }),
                    /*#__PURE__*/ _jsx(EditableField, {
                        icon: /*#__PURE__*/ _jsx(Truck, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "Vehicle",
                        value: sol.contractVehicle,
                        displayValue: VEHICLE_OPTIONS.find((o)=>o.value === sol.contractVehicle)?.label || sol.contractVehicle,
                        options: VEHICLE_OPTIONS,
                        onSave: (v)=>handleFieldSave('contractVehicle', v),
                        placeholder: "Search vehicles..."
                    }),
                    /*#__PURE__*/ _jsx(EditableField, {
                        icon: /*#__PURE__*/ _jsx(FileSignature, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "Contract Type",
                        value: sol.contractType,
                        options: CONTRACT_TYPE_OPTIONS,
                        onSave: (v)=>handleFieldSave('contractType', v),
                        placeholder: "Search types..."
                    }),
                    /*#__PURE__*/ _jsx(EditableField, {
                        icon: /*#__PURE__*/ _jsx(ShieldCheck, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "Set-Aside",
                        value: sol.setAside,
                        displayValue: formatSetAside(sol.setAside || ''),
                        options: SET_ASIDE_OPTIONS,
                        onSave: (v)=>handleFieldSave('setAside', v),
                        placeholder: "Search set-asides..."
                    }),
                    /*#__PURE__*/ _jsx(EditableField, {
                        icon: /*#__PURE__*/ _jsx(Hash, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "NAICS",
                        value: sol.naicsCode,
                        displayValue: sol.naicsCode ? `${sol.naicsCode} — ${NAICS_CODES.find((n)=>n.value === sol.naicsCode)?.description || ''}` : undefined,
                        options: NAICS_CODES,
                        onSave: (v)=>handleFieldSave('naicsCode', v),
                        placeholder: "Search NAICS codes..."
                    }),
                    sol.office && /*#__PURE__*/ _jsx(SummaryChip, {
                        icon: /*#__PURE__*/ _jsx(MapPin, {
                            className: "h-3.5 w-3.5"
                        }),
                        label: "Office",
                        value: sol.office
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Section, {
                title: "Evaluation scheme",
                icon: /*#__PURE__*/ _jsx(Trophy, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                children: insights.isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "h-16 animate-shimmer rounded-lg bg-slate-100"
                }) : evalScheme ? /*#__PURE__*/ _jsx(EvaluationSummary, {
                    scheme: evalScheme
                }) : /*#__PURE__*/ _jsx(EmptyState, {
                    body: "No evaluation scheme extracted yet. Process the solicitation to surface factors here."
                })
            }),
            /*#__PURE__*/ _jsx(Section, {
                title: "Volumes",
                icon: /*#__PURE__*/ _jsx(BookOpen, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                children: volumes.isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "space-y-2",
                    children: [
                        0,
                        1,
                        2
                    ].map((i)=>/*#__PURE__*/ _jsx("div", {
                            className: "h-10 animate-shimmer rounded-lg bg-slate-100"
                        }, i))
                }) : (volumes.data?.length ?? 0) === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                    body: "No volumes have been identified yet. They'll appear here after extraction or once you create one."
                }) : /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-1.5",
                    children: (volumes.data ?? []).map((v)=>/*#__PURE__*/ _jsxs(Link, {
                            to: `/proposal/${projectId}/v2/volume/${v.volumeId}`,
                            className: "flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm transition-colors hover:border-slate-200 hover:bg-slate-50",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "min-w-0 truncate text-slate-800",
                                    children: v.displayName
                                }),
                                v.isCustom && /*#__PURE__*/ _jsx("span", {
                                    className: "flex-shrink-0 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-px text-[10px] font-medium text-violet-700",
                                    children: "Custom"
                                })
                            ]
                        }, v.volumeId))
                })
            }),
            /*#__PURE__*/ _jsx(Section, {
                title: "Recent activity",
                icon: /*#__PURE__*/ _jsx(Activity, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                children: activity.isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "space-y-2",
                    children: [
                        0,
                        1,
                        2
                    ].map((i)=>/*#__PURE__*/ _jsx("div", {
                            className: "h-9 animate-shimmer rounded-lg bg-slate-100"
                        }, i))
                }) : (activity.data?.entries.length ?? 0) === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                    icon: /*#__PURE__*/ _jsx(Activity, {
                        className: "h-5 w-5 text-slate-400",
                        "aria-hidden": "true"
                    }),
                    body: "No activity yet. The first team change will appear here."
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("ul", {
                            className: "space-y-1.5",
                            children: (activity.data?.entries ?? []).slice(0, 5).map((e)=>/*#__PURE__*/ _jsxs("li", {
                                    className: "rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm text-slate-700",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "truncate",
                                            children: e.summary
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-0.5 text-[11px] text-slate-500",
                                            children: e.at?.toDate().toLocaleString('en-US', {
                                                dateStyle: 'medium',
                                                timeStyle: 'short'
                                            })
                                        })
                                    ]
                                }, e.id))
                        }),
                        (activity.data?.totalCount ?? 0) > 5 && /*#__PURE__*/ _jsxs(Link, {
                            to: `/proposal/${projectId}/v2/compliance`,
                            className: "mt-2 flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800",
                            children: [
                                "View full activity log",
                                /*#__PURE__*/ _jsx(ChevronRight, {
                                    className: "h-3 w-3"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function Section({ title, icon, children }) {
    return /*#__PURE__*/ _jsxs("section", {
        className: "rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex items-center gap-2 text-sm font-semibold text-slate-900",
                children: [
                    icon,
                    /*#__PURE__*/ _jsx("span", {
                        children: title
                    })
                ]
            }),
            children
        ]
    });
}
function SummaryChip({ icon, label, value }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "inline-flex max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-slate-500",
                children: icon
            }),
            /*#__PURE__*/ _jsxs("span", {
                className: "text-slate-500",
                children: [
                    label,
                    ":"
                ]
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "truncate font-medium text-slate-800",
                children: value
            })
        ]
    });
}
function EvaluationSummary({ scheme }) {
    const factors = scheme.factors ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            scheme.method && /*#__PURE__*/ _jsx("div", {
                className: "mb-2 inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-blue-700",
                children: scheme.method.replace(/_/g, ' ')
            }),
            factors.length === 0 ? /*#__PURE__*/ _jsx("div", {
                className: "text-xs text-slate-500",
                children: "No factors identified."
            }) : /*#__PURE__*/ _jsxs("ul", {
                className: "space-y-1.5",
                children: [
                    factors.slice(0, 5).map((f, idx)=>/*#__PURE__*/ _jsxs("li", {
                            className: "flex items-baseline justify-between gap-3 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "min-w-0 truncate text-slate-800",
                                    children: f.name
                                }),
                                f.weight ? /*#__PURE__*/ _jsx("span", {
                                    className: "flex-shrink-0 text-xs text-slate-500",
                                    children: f.weight
                                }) : null
                            ]
                        }, `${f.name}-${idx}`)),
                    factors.length > 5 && /*#__PURE__*/ _jsxs("li", {
                        className: "text-[11px] italic text-slate-500",
                        children: [
                            "+",
                            factors.length - 5,
                            " more"
                        ]
                    })
                ]
            })
        ]
    });
}
const SET_ASIDE_LABELS = {
    small_business: 'Small Business',
    sdvosb: 'SDVOSB',
    wosb: 'WOSB',
    hubzone: 'HUBZone',
    '8a': '8(a)',
    unrestricted: 'Unrestricted',
    total_small_business: 'Total Small Business'
};
function formatSetAside(raw) {
    return SET_ASIDE_LABELS[raw] || raw.replace(/_/g, ' ');
}
function EmptyState({ icon, body }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-start gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/40 px-4 py-3 text-xs text-slate-600",
        children: [
            icon,
            /*#__PURE__*/ _jsx("span", {
                children: body
            })
        ]
    });
}
